import { Values, FormFields } from '../types'
import { FormField } from '@generated'
import { useFormikContext } from 'formik'
import { FC } from 'react'
import { AccountCreationField } from './AccountCreationField'
import { AddressFields } from './AddressFields'
import { AttendanceFields } from './AttendanceFields'
import { BasicFields } from './BasicFields'
import { EventSpecificFields } from './EventSpecificFields'
import { MessageField } from './MessageField'
import { NewsletterField } from './NewsletterField'
import { PhoneField } from './PhoneField'
import { PrivacyPolicyField } from './PrivacyPolicyField'
import { isFieldEnabled, getFieldValue } from '../utils'

type FieldsProps = {
  formId: string
  fields: FormField[] | FormFields
  selectableDates?: string[]
  selectableTimes?: string[]
  maxPersons?: number
  newsletterText?: String
  showAttendance?: boolean
  isNewApiStructure?: boolean
}

export const Fields: FC<FieldsProps> = ({
  formId,
  fields,
  selectableDates: propSelectableDates,
  selectableTimes: propSelectableTimes,
  maxPersons: propMaxPersons = 50,
  showAttendance = true,
  isNewApiStructure = false,
}) => {
  const { values } = useFormikContext<Values>()

  const willAttend = showAttendance ? values.attendance === true : true

  let actualSelectableDates: string[] = []
  let actualSelectableTimes: string[] = []
  let actualMaxPersons: number = propMaxPersons

  if (isNewApiStructure) {
    const formFields = fields as FormFields
    actualSelectableDates = getFieldValue(formFields, FormField.Date) ?? []
    actualSelectableTimes = getFieldValue(formFields, FormField.Time) ?? []
    actualMaxPersons = getFieldValue(formFields, FormField.NumberOfPersons) ?? 50
  } else {
    actualSelectableDates = propSelectableDates ?? []
    actualSelectableTimes = propSelectableTimes ?? []
  }

  const isFieldEnabledInStructure = (fieldType: FormField): boolean => {
    if (isNewApiStructure) {
      const formFields = fields as FormFields
      return isFieldEnabled(formFields, fieldType)
    } else {
      const inputFields = fields as FormField[]
      return inputFields.includes(fieldType)
    }
  }

  return (
    <>
      {showAttendance && <AttendanceFields />}
      <BasicFields />

      {willAttend ? (
        <>
          {isFieldEnabledInStructure(FormField.HomeAddress) && (
            <AddressFields
              isNewApiStructure={isNewApiStructure}
              fields={isNewApiStructure ? (fields as FormFields) : undefined}
            />
          )}
          {isFieldEnabledInStructure(FormField.PhoneNumber) && (
            <PhoneField
              isNewApiStructure={isNewApiStructure}
              fields={isNewApiStructure ? (fields as FormFields) : undefined}
            />
          )}
          <EventSpecificFields
            fields={fields}
            selectableDates={actualSelectableDates}
            selectableTimes={actualSelectableTimes}
            maxPersons={actualMaxPersons}
            isNewApiStructure={isNewApiStructure}
          />
          {isFieldEnabledInStructure(FormField.Message) && (
            <MessageField
              isNewApiStructure={isNewApiStructure}
              fields={isNewApiStructure ? (fields as FormFields) : undefined}
            />
          )}
          {(isFieldEnabledInStructure(FormField.NewsletterMandatory) ||
            isFieldEnabledInStructure(FormField.GassanPlazaNews)) && (
            <NewsletterField
              formId={formId}
              isNewApiStructure={isNewApiStructure}
              fields={isNewApiStructure ? (fields as FormFields) : undefined}
            />
          )}
          {isFieldEnabledInStructure(FormField.CreateAccount) && <AccountCreationField />}
        </>
      ) : (
        <MessageField
          isNewApiStructure={isNewApiStructure}
          fields={isNewApiStructure ? (fields as FormFields) : undefined}
        />
      )}
      <PrivacyPolicyField formId={formId} />
    </>
  )
}
